import {
  Box,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  SxProps,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { capitalizeWords } from "@utils/stringFormat";
import { ChevronDown, ChevronUp } from "lucide-react";
import useLocales from "src/hooks/useLocales";

export type HeadLabel = {
  id?: any;
  label?: string;
  align?: string;
  width?: string | number;
  minWidth?: string | number;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  sortable?: boolean;
  sx?: SxProps;
  preserveCase?: boolean;
};

// Backward-compat alias used by Finance/AP/AR pages
export type THeadLabel = HeadLabel;

type Props = {
  order?: "asc" | "desc";
  orderBy?: string;
  headLabel: HeadLabel[];
  rowCount?: number;
  numSelected?: number;
  onSort?: (id: string, type: 0 | 1) => void;
  onSelectAllRows?: (checked: boolean) => void;
  executionOrder?: boolean;
  pinFirstColumn?: boolean;
  pinLastColumn?: boolean;
  disabled?: boolean;
  loader?: boolean;
  sx?: SxProps<Theme>;
  cmpT?: (key: string) => string;
};

export default function TableHeadCustom({
  order,
  orderBy,
  rowCount = 0,
  headLabel,
  numSelected = 0,
  onSort,
  onSelectAllRows,
  executionOrder = false,
  pinFirstColumn = false,
  pinLastColumn = false,
  disabled = false,
  loader = false,
  sx,
  cmpT = (key: string) => key,
}: Props) {
  const { palette } = useTheme();
  const { translate } = useLocales();

  const finalHeadLabel = executionOrder
    ? [
        {
          id: "executionOrder",
          label: translate("order"),
          align: "left" as const,
          width: 120,
        },
        ...headLabel,
      ]
    : headLabel;

  return (
    <TableHead sx={{ borderRadius: 1, ...sx }}>
      <TableRow
        sx={{
          "& .MuiTableCell-root": {
            px: 1.5,
            py: 1.5,
            backgroundColor: palette.text.input,
            borderBottom: `1px solid ${palette.text.borderLine}`,
          },
        }}
      >
        {onSelectAllRows && finalHeadLabel.length !== 0 && (
          <TableCell
            padding="checkbox"
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 5,
              backgroundColor: palette.text.input,
            }}
          >
            {loader ? (
              <Skeleton variant="rectangular" width={15} height={15} />
            ) : (
              <Checkbox
                size="small"
                disabled={disabled}
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={(e) => onSelectAllRows(e.target.checked)}
              />
            )}
          </TableCell>
        )}

        {finalHeadLabel.map((headCell, index) => {
          const isFirst = index === 0;
          const isLast = index === finalHeadLabel.length - 1;
          const shouldCenter = executionOrder && (isFirst || isLast);
          const formattedLabel = headCell.preserveCase
            ? headCell.label
            : capitalizeWords(headCell.label);

          return (
            <TableCell
              key={headCell.id ?? index}
              align={(shouldCenter ? "center" : headCell.align ?? "left") as any}
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{
                verticalAlign: "middle",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: headCell.width,
                minWidth: headCell.minWidth ?? (!isLast ? 140 : undefined),
                ...(headCell.sx ?? {}),

                ...(pinFirstColumn &&
                  isFirst && {
                    position: "sticky",
                    left: onSelectAllRows ? 48 : 0,
                    zIndex: 4,
                    backgroundColor: palette.text.input,
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                  }),

                ...(pinLastColumn &&
                  isLast && {
                    position: "sticky",
                    right: 0,
                    zIndex: 4,
                    backgroundColor: palette.text.input,
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                  }),
              }}
            >
              {loader ? (
                <Skeleton width={90} />
              ) : onSort && headCell.sortable ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                >
                  {cmpT ? cmpT(formattedLabel ?? "") : formattedLabel}
                  <Stack spacing={0} sx={{ ml: 0.5 }}>
                    <IconButton
                      size="small"
                      sx={{ p: 0, height: 12 }}
                      onClick={() => onSort(headCell.id, 0)}
                    >
                      <ChevronUp
                        size={14}
                        color={
                          orderBy === headCell.id && order === "asc"
                            ? palette.primary.main
                            : palette.text.label
                        }
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ p: 0, height: 12 }}
                      onClick={() => onSort(headCell.id, 1)}
                    >
                      <ChevronDown
                        size={14}
                        color={
                          orderBy === headCell.id && order === "desc"
                            ? palette.primary.main
                            : palette.text.label
                        }
                      />
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent:
                      shouldCenter || headCell.align === "center"
                        ? "center"
                        : headCell.align === "right"
                        ? "flex-end"
                        : "flex-start",
                  }}
                >
                  {headCell.startIcon}
                  {cmpT ? cmpT(formattedLabel ?? "") : formattedLabel}
                  {headCell.endIcon}
                </Box>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
